import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";
import "./Table.css"; // Assuming you still want basic table styling
import { saveAs } from 'file-saver';
import {useNavigate} from "react-router-dom";


const OutletList = () => {
    const [outletData, setOutletData] = useState([]);
    const [pincodeFilter, setPincodeFilter] = useState("");

    const navigate = useNavigate();

    const columnHelper = createColumnHelper();

    const columns = useMemo(
        () => [
            columnHelper.accessor("gccNum", { // Directly accessing, assuming data structure
                header: () => "GCC Code",
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("outletName", {
                header: () => "Outlet Name",
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("pinCode", {
                header: () => "Pin Code",
                cell: (info) => info.getValue(),
                filterFn: (row, columnId, filterValue) => {
                    return String(row.getValue(columnId)).startsWith(filterValue);
                },
            }),
            columnHelper.accessor("address", {
                header: () => "Address",
                cell: (info) => info.getValue(),
            }),
            columnHelper.display({
                id: "menu",
                header: () => "",
                cell: ({ row }) => (
                    <><button onClick={() => navigate(`/tasksList/all/?outlet=${row.original._id}`)} className="btn btn-primary">  <span className="text-bold text-xl"> ↸ </span>View Tasks</button></>
                )
            })
            // Add any other relevant outlet information columns here.
        ],
        []
    );



    const table = useReactTable({
        data: outletData,
        columns,
        state: {
            globalFilter: pincodeFilter,
        },
        onGlobalFilterChange: setPincodeFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });


    const getOutletList = async () => {
        const userToken = localStorage.getItem("user-token");

        const headers = {
            "authorization": userToken,
        };
        let bUrl = `/outlet/search`; //  Endpoint for outlets

        try {
            const response = await axios.get(bUrl, {
                headers: headers,
                params: { search: pincodeFilter }, // Pass the search query
            });

            setOutletData(response.data.data); // Assuming the API returns { data: [outlets] }
        } catch (error) {
            console.error("Error fetching outlet data:", error);
            // Optionally handle the error (e.g., display an error message)
        }
    };

    const downloadCSV = () => {
        if (!outletData || outletData.length === 0) {
            alert("No data to download.");
            return;
        }

        const headers = Object.keys(outletData[0]); // Get headers from the first object
        const csvRows = [];

        csvRows.push(headers.join(',')); // Add headers

        for (const row of outletData) {
            const values = headers.map(header => {
                let value = row[header];
                if (value === null || value === undefined) {
                    value = '';
                }

                // Escape commas and quotes
                let processedValue = String(value).replace(/"/g, '""');
                if (processedValue.includes(',') || processedValue.includes('"')) {
                    processedValue = `"${processedValue}"`;
                }
                return processedValue;
            });
            csvRows.push(values.join(','));
        }

        const csvData = csvRows.join('\n');

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'outlets.csv');
    };



    useEffect(() => {
        getOutletList();
    }, [pincodeFilter]); //  Fetch data when the component mounts and when pincodeFilter changes



    return (
        <div className={"flex"}>
            <div className="w-[90vw] ml-[7vw] mt-[2vh] mr-0 overflow-auto rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold mb-4">Outlets</h1>
                    <button onClick={downloadCSV} className="btn btn-primary">
                        <span className="text-bold text-xl"> ↯ </span> Download CSV
                    </button>
                </div>
                <div className="p-4">
                    <input
                        type="text"
                        value={pincodeFilter}
                        onChange={(e) => setPincodeFilter(e.target.value)}
                        placeholder="Search by Pincode, GCC, address, outlet name..."
                        className="input input-bordered w-full"
                    />
                </div>
                <div className="overflow-x-auto">
                    <table className="table w-full">
                        <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.column.getCanSort() ? "cursor-pointer select-none" : ""}`}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        {{
                                            asc: " ↑",
                                            desc: " ↓",
                                        }[header.column.getIsSorted()] ?? null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                className="hover:bg-gray-100 cursor-pointer transition-colors duration-200"
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                <div className="join p-4">
                    <button
                        className="join-item btn"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<<"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {">"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        {">>"}
                    </button>
                    <span style={{alignItems: 'center'}} className="flex input input-bordered join-item">
            Page
                        {' '}
                        <strong>
              {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
            </strong>
          </span>
                    <span className="flex items-center gap-1">

            <input
                type="number"
                min={1}
                max={table.getPageCount()}
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    table.setPageIndex(page);
                }}
                className="input input-bordered w-24"
            />
          </span>
                    <select
                        className="select select-bordered"
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

export default OutletList;
